import type { HubConnection } from '@microsoft/signalr';

let globalSignalRConnection: HubConnection | null = null;

export const getGlobalSignalRConnection = (): HubConnection | null =>
  globalSignalRConnection;

export const setGlobalSignalRConnection = (
  connection: HubConnection | null
): void => {
  globalSignalRConnection = connection;
};

/**
 * Ensures the shared SignalR connection is active before an AI HTTP request is
 * submitted. Reconnection retries are bounded so callers receive a rejection
 * rather than submitting a response that cannot be streamed.
 */
export const ensureSignalRConnected = async (
  maxRetries = 3
): Promise<boolean> => {
  const connection = globalSignalRConnection;
  if (!connection) {
    console.log('[SignalR] No global connection exists, skipping ensure.');
    return false;
  }

  if (connection.state === 'Connected') {
    return true;
  }

  if (connection.state === 'Connecting' || connection.state === 'Reconnecting') {
    console.log(`[SignalR] Currently ${connection.state}, waiting...`);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    return globalSignalRConnection?.state === 'Connected';
  }

  if (connection.state === 'Disconnected') {
    console.log('[SignalR] Disconnected, attempting to reconnect...');

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        await Promise.race([
          connection.start(),
          new Promise<never>((_, reject) =>
            setTimeout(
              () => reject(new Error('SignalR start timeout')),
              10_000
            )
          ),
        ]);
        console.log('[SignalR] Reconnected successfully');
        return true;
      } catch (error) {
        console.error(
          `[SignalR] Reconnect attempt ${attempt}/${maxRetries} failed:`,
          error
        );

        if (attempt < maxRetries) {
          const backoffMs = Math.pow(2, attempt - 1) * 1000;
          await new Promise((resolve) => setTimeout(resolve, backoffMs));
        }
      }
    }

    console.error('[SignalR] Failed to reconnect after max retries');
    return false;
  }

  console.warn(`[SignalR] Unexpected connection state: ${connection.state}`);
  return false;
};
